import { BasicColumnTypes } from '@opengov/ogql-types';

import { IEntity, IReport } from './report';
import { EReportColumnTypes, CalendarUnit } from './reportColumns';
import { IReportConfiguration } from './reportConfiguration';
import { ISavedView } from './reportSavedView';

type IDataset = {
  created?: string;
  name: string;
  description?: string;
  etag?: string;
  id: string;
  file_name?: string;
  updated_at?: string;
  data_updated_at?: string;
  year?: number;
  month?: number;
  cumulative?: boolean;
  data_set_name?: string;
  created_at?: string;
  source_dataset_id?: string;
  added_to_report_at?: string;
  calendar_month?: number | string;
  coa_id?: string;
  order?: number;
  fiscal_year?: number;
  data_set_type?: string;
  fiscal_period?: number;
  calendar_year?: number | string;
  column_mapping?: {
    [key: string]: {
      name: string;
      type: string;
      special_type: string;
      special_types: string[];
      optional: boolean;
      dependent_column_type: string;
    };
  };
};

export enum TableContentType {
  RowHeader = 'rowHeader',
  RowSubHeader = 'rowSubHeader',
  RowGroupHeader = 'rowGroupHeader',
  RowGroupTotal = 'rowGroupTotal',
  RowWithoutLabel = 'RowWithoutLabel',
  Row = 'row',
  Col = 'col'
}

export enum ESummaryTableRequest {
  summaryTableType = 'SUMMARY_TABLE_EMBED_REQUEST'
}

export enum ESummaryTableClassicResponseLabels {
  CUMULATIVE = 'cumulative',
  TOTAL = 'Total',
  NO_SERIES_NAME = '- Blank -'
}

export enum ESummaryTableNextGenDataFormat {
  CURRENCY = 'currency',
  PERCENT = 'percentage',
  NUMERIC = 'numeric',
  TEXT = 'text',
  INTEGER = 'integer',
  NONE = ''
}

export enum ESummaryTableClassicDataFormat {
  MONETARY = 'monetary',
  PERCENTAGE = 'percent',
  NUMERIC = 'numeric',
  CURRENCY = 'currency'
}

export enum EReportType {
  YTD = 'ytd',
  BALANCE = 'balance'
}

export enum EBreakdownType {
  TYPES = 'types'
}

export type ClassicGridReportCellFormat = ESummaryTableNextGenDataFormat.CURRENCY | EReportColumnTypes;

export interface ISummaryTableCol {
  id: string;
  value: string;
  format?: ESummaryTableClassicDataFormat | ESummaryTableNextGenDataFormat | BasicColumnTypes;
  colSpan?: number;
  label?: string;
  calendarUnit?: CalendarUnit | null;
}

export interface ISummaryTableRow {
  id: string;
  type: TableContentType;
  ledger?: ELedger;
  level?: number;
  label?: string;
  name?: string;
  rows?: ISummaryTableRow[];
  col?: ISummaryTableCol[];
  format?: ESummaryTableNextGenDataFormat | ESummaryTableClassicDataFormat | BasicColumnTypes;
  /**
   * True only for calculated/summary row entries (e.g. "Net Revenue"), which collapse to a
   * single row with no nested `rows` — structurally identical to a regular row group that has
   * no breakdown. This flag lets consumers (e.g. summaryTableResultToCsv) place calc rows
   * distinctly from regular collapsed rows without misclassifying the latter.
   */
  isCalculated?: boolean;
}

export interface ISummaryTableResult {
  columns?: ISummaryTableCol[];
  content: ISummaryTableRow[];
}

export interface ISummaryTableJobResult {
  data: ISummaryTableResult;
  report: IReport;
  savedView: ISavedView;
  reportConfiguration: IReportConfiguration;
  entity: IEntity;
}

export enum ELedger {
  REVENUES = 'revenues',
  EXPENSES = 'expenses',
  ASSETS = 'assets',
  LIABILITIES = 'liabilities',
  BALANCESHEET = 'balanceSheet',
  REVENUESVSEXPENSES = 'revenuesVersusExpenses',
  FTECOUNT = 'fteCount'
}

export type IDatasetClassic = IDataset & {
  type?: string;
  label?: string;
  previousDataSetId?: string;
  prevProjectedDataSetId?: string;
  lastActualDataSetId?: string;
  lastOverlappingProjectionDataSetId?: string;
  showVariance?: boolean;
  compareToIndex?: number;
  varianceColumnName?: string;
};
export interface ISummaryTableClassicFilteredDatasets {
  [key: string]: IDatasetClassic;
}

export type ITableData = ISummaryTableResult & { dataUpdatedAt: string };
